/**
 * ─── Arena Dashboard ────────────────────────────────────────
 * Main overview for the fighting arena: upcoming matches,
 * fighter stats, top members, recent bets, revenue.
 */

import { useEffect, useState, useCallback } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardHeader, Badge, Button } from '@repo/ui-core'
import {
  arenaRepo, arenaFighterRepo, arenaMatchRepo,
  arenaMemberRepo, arenaBetRepo, arenaEntranceRepo,
} from '../../lib/db'
import {
  MATCH_STATUS_LABELS, FIGHTER_STATUS_LABELS, BET_STATUS_LABELS,
} from '@repo/entity-arena'
import {
  Swords, Trophy, Users, Coins, Ticket, TrendingUp,
  Calendar, ArrowRight, Plus,
} from 'lucide-react'

export function ArenaDashboardPage() {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    fighters: 0, matches: 0, members: 0, bets: 0,
    totalBetAmount: 0, totalEntrance: 0, upcomingMatches: 0,
  })
  const [upcomingMatches, setUpcomingMatches] = useState<any[]>([])
  const [recentBets, setRecentBets] = useState<any[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [fighters, matches, members, bets, entrances] = await Promise.all([
        arenaFighterRepo.findMany({ page: 1, pageSize: 500 }),
        arenaMatchRepo.findMany({ page: 1, pageSize: 100, sort: [{ field: 'scheduledDate', direction: 'desc' }] }),
        arenaMemberRepo.findMany({ page: 1, pageSize: 500 }),
        arenaBetRepo.findMany({ page: 1, pageSize: 100, sort: [{ field: 'createdAt', direction: 'desc' }] }),
        arenaEntranceRepo.findMany({ page: 1, pageSize: 500 }),
      ])

      const fItems: any[] = 'items' in fighters ? fighters.items : []
      const mItems: any[] = 'items' in matches ? matches.items : []
      const memItems: any[] = 'items' in members ? members.items : []
      const bItems: any[] = 'items' in bets ? bets.items : []
      const eItems: any[] = 'items' in entrances ? entrances.items : []

      setStats({
        fighters: fItems.length,
        matches: mItems.length,
        members: memItems.length,
        bets: bItems.length,
        totalBetAmount: bItems.reduce((s: number, b: any) => s + (b.amount ?? 0), 0),
        totalEntrance: eItems.reduce((s: number, e: any) => s + (e.totalAmount ?? 0), 0),
        upcomingMatches: mItems.filter((m: any) => m.status === 'scheduled').length,
      })

      setUpcomingMatches(mItems.filter((m: any) => m.status === 'scheduled').slice(0, 5))
      setRecentBets(bItems.slice(0, 5))
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Fighting Arena</h2>
          <p className="mt-1 text-sm text-gray-500">Arena management — fighters, matches, membership, betting, entrance</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Fighters', stats.fighters, 'red', Swords],
          ['Matches', stats.matches, 'purple', Trophy],
          ['Members', stats.members, 'blue', Users],
          ['Bets Placed', stats.bets, 'green', Coins],
          ['Total Wagered', `₱${stats.totalBetAmount.toLocaleString()}`, 'amber', TrendingUp],
          ['Entrance Revenue', `₱${stats.totalEntrance.toLocaleString()}`, 'cyan', Ticket],
          ['Upcoming', stats.upcomingMatches, 'indigo', Calendar],
        ].map(([label, value, color, Icon]: any) => (
          <div key={label} className={`rounded-xl border border-${color}-200 bg-${color}-50 p-4`}>
            <div className="flex items-center gap-2">
              {Icon && <Icon className={`h-4 w-4 text-${color}-600`} />}
              <p className={`text-xs text-${color}-600`}>{label}</p>
            </div>
            <p className={`mt-1 text-xl font-bold text-${color}-700`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Upcoming Matches */}
        <Card>
          <CardHeader
            title="Upcoming Matches"
            description={`${upcomingMatches.length} scheduled`}
            action={
              <Link to="/arena/matches" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                View All <ArrowRight className="h-3 w-3" />
              </Link>
            }
          />
          <div className="divide-y divide-gray-100">
            {upcomingMatches.length === 0 ? (
              <p className="p-4 text-sm text-gray-400">No upcoming matches. Schedule one!</p>
            ) : upcomingMatches.map((m: any) => (
              <div key={m.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-900">{m.matchCode}</p>
                  <p className="text-xs text-gray-500">
                    {m.fighter1Name ?? 'Fighter 1'} vs {m.fighter2Name ?? 'Fighter 2'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">
                    {m.scheduledDate ? new Date(m.scheduledDate).toLocaleDateString() : 'TBD'}
                  </p>
                  <Badge color="yellow">{MATCH_STATUS_LABELS[m.status as keyof typeof MATCH_STATUS_LABELS] ?? m.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Bets */}
        <Card>
          <CardHeader
            title="Recent Bets"
            description={`${recentBets.length} latest`}
            action={
              <Link to="/arena/bets" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                View All <ArrowRight className="h-3 w-3" />
              </Link>
            }
          />
          <div className="divide-y divide-gray-100">
            {recentBets.length === 0 ? (
              <p className="p-4 text-sm text-gray-400">No bets placed yet.</p>
            ) : recentBets.map((b: any) => (
              <div key={b.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-900">{b.memberName ?? b.memberId?.slice(0, 8)}</p>
                  <p className="text-xs text-gray-500">on {b.fighterName ?? b.fighterId?.slice(0, 8)} @ {b.odds?.toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-900">₱{(b.amount ?? 0).toLocaleString()}</p>
                  <Badge color={b.status === 'won' ? 'green' : b.status === 'lost' ? 'red' : 'yellow'}>
                    {BET_STATUS_LABELS[b.status as keyof typeof BET_STATUS_LABELS] ?? b.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  )
}
